/**
 * The HR employee-document domain's client-side vocabulary, and the two flows the screen
 * cannot express as a plain `useApiQuery`/`useApiMutation`: **register-then-upload** an
 * HR document, and the **access-reason-gated download** of one.
 *
 * This mirrors `recruiting/candidate.ts` exactly, because the HR employee-document
 * surface is the same shape as the candidate-CV one — a presigned-PUT upload, and a
 * single sensitive, access-logged download gated on `X-Access-Reason`. The one
 * difference is the stronger read code the server enforces (`hr.view_employee_documents`
 * rather than `recruiting.view_candidate_documents`), which is a server fact this client
 * neither knows nor re-decides.
 *
 * ── Why the download and upload are functions here, not hooks in the screen ──
 *
 * Both are two-step and neither fits the one-request shape of `useApiMutation`. Upload
 * is `POST …/documents` (register, which returns a presigned PUT URL) *then* a `PUT` of
 * the bytes straight to object storage. Download is a `fetch` with `X-Access-Reason`
 * whose `302` the browser follows to a signed GET — a call `api/client.ts` makes with
 * its own `sensitiveDownload`, because the shared `request` refuses redirects. Keeping
 * the orchestration here, behind small pure-ish functions, is what makes both testable
 * without a DOM (the screen injects the one side effect each has).
 *
 * ── The reason is checked here before a request goes out ────────────────────
 *
 * `reason.ts`'s whole argument: a screen that fires a request it knows will be refused
 * teaches people that refusals are noise. So `downloadEmployeeDocument` validates the
 * reason with `isUsableReason` and, when it is missing or too short, returns the
 * `WORKFORCE_ACCESS_REASON_REQUIRED` refusal **without a request** — the same code the
 * server would answer, which `ApiErrorNote` renders as the permission-denied state.
 * Fail closed, and learn the rule from the field.
 */

import type { ApiClient } from '../api/client';
import { CLIENT_TRANSPORT_FAILED, WorkforceApiError } from '../api/errors';
import type { MessageKey } from '../i18n/messages';
import {
  hrEmployeeDocumentDownloadPath,
  hrEmployeeDocumentsPath,
  type HrContractStatus,
  type HrContractType,
  type HrDocumentType,
  type HrEmployeeDocument,
  type HrEmployeeDocumentRegisterRequest,
  type HrEmployeeDocumentUpload,
} from '../api/hr';
import { isUsableReason } from '../access/reason';
// The browser side effect and its type are generic; reused rather than re-implemented.
import { deliverBlobDownload, type DeliverBlob } from '../recruiting/candidate';

export { deliverBlobDownload, type DeliverBlob };

/** The name of a document's `doc_type`, for the documents list and the register form. */
export const HR_DOCUMENT_TYPE_LABEL: Readonly<Record<HrDocumentType, MessageKey>> = {
  id_card: 'hr.documents.docType.id_card',
  passport: 'hr.documents.docType.passport',
  contract: 'hr.documents.docType.contract',
  certificate: 'hr.documents.docType.certificate',
  tax: 'hr.documents.docType.tax',
  other: 'hr.documents.docType.other',
};

/** The name of a contract's `contract_type`, for the contracts list and the create form. */
export const HR_CONTRACT_TYPE_LABEL: Readonly<Record<HrContractType, MessageKey>> = {
  permanent: 'hr.contracts.type.permanent',
  fixed_term: 'hr.contracts.type.fixed_term',
  probation: 'hr.contracts.type.probation',
  intern: 'hr.contracts.type.intern',
};

/** The name of a contract's `status`, and the badge severity it reads as. */
export const HR_CONTRACT_STATUS_LABEL: Readonly<Record<HrContractStatus, MessageKey>> = {
  draft: 'hr.contracts.status.draft',
  active: 'hr.contracts.status.active',
  expired: 'hr.contracts.status.expired',
  terminated: 'hr.contracts.status.terminated',
};

/* ────────────────────────────────────── register + upload a document (WP-W6) ── */

/** What the screen may know about a register-and-upload that has run or is running. */
export type UploadState =
  | { readonly status: 'idle' }
  | { readonly status: 'working' }
  | { readonly status: 'done'; readonly document: HrEmployeeDocument }
  | { readonly status: 'failed'; readonly error: WorkforceApiError };

/**
 * Register an HR document's metadata, then upload its bytes to the presigned PUT URL the
 * register answered with. Two awaits, one outcome: a failure in either step is the one
 * `error` the form shows, and the bytes never touch this app's server (`openapi.yaml`:
 * "The server never proxies the bytes").
 *
 * `content_type` and `file_size_bytes` are sent only when the browser knows them, per
 * the present-or-absent rule — an empty `File.type` is omitted, not sent as `''`.
 */
export async function registerAndUploadDocument(
  client: Pick<ApiClient, 'request' | 'putPresigned'>,
  employeeId: string,
  file: File,
  docType: HrDocumentType,
): Promise<UploadState> {
  try {
    const body: HrEmployeeDocumentRegisterRequest = {
      filename: file.name,
      doc_type: docType,
      ...(file.type === '' ? {} : { content_type: file.type }),
      ...(Number.isFinite(file.size) ? { file_size_bytes: file.size } : {}),
    };
    const upload = await client.request<HrEmployeeDocumentUpload>(
      hrEmployeeDocumentsPath(employeeId),
      { method: 'POST', body },
    );
    // Spent immediately and never stored — a bearer credential with a short life.
    await client.putPresigned(upload.upload_url, file);
    return { status: 'done', document: upload.document };
  } catch (cause) {
    return { status: 'failed', error: asWorkforceError(cause, 'upload failed') };
  }
}

/* ─────────────────────────────── the access-reason-gated document download (WP-W6) ── */

/** What a screen may remember about a download it asked for. Deliberately holds no URL. */
export type DownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'requesting'; readonly docId: string }
  | { readonly status: 'delivered'; readonly docId: string; readonly filename: string }
  | { readonly status: 'refused'; readonly docId: string; readonly error: WorkforceApiError };

/**
 * The `403 WORKFORCE_ACCESS_REASON_REQUIRED` this client raises **itself** when the
 * reason is missing or too short — the same code the server answers, so `ApiErrorNote`
 * renders the same permission-denied state whichever raised it. The message is never
 * shown (`PermissionDenied` keys off the code), but it is a true sentence anyway.
 */
export function accessReasonRefusal(): WorkforceApiError {
  return new WorkforceApiError({
    code: 'WORKFORCE_ACCESS_REASON_REQUIRED',
    message: 'An access reason of at least ten characters is required to open an HR document.',
    status: 403,
  });
}

/**
 * Open one employee HR document, on the record.
 *
 * The reason is validated **before** a request goes out; a bad one is refused here with
 * no `wp_data_access_logs` row possible, because none is written for a request that was
 * never made. A good one is sent — trimmed, as the server trims before measuring — as
 * `X-Access-Reason`; the browser follows the `302` and the bytes are handed to
 * `deliver`. The signed URL never reaches this function.
 */
export async function downloadEmployeeDocument(
  client: Pick<ApiClient, 'sensitiveDownload'>,
  employeeId: string,
  doc: HrEmployeeDocument,
  reason: string,
  deliver: DeliverBlob,
): Promise<DownloadState> {
  if (!isUsableReason(reason)) {
    return { status: 'refused', docId: doc.id, error: accessReasonRefusal() };
  }
  try {
    const blob = await client.sensitiveDownload(
      hrEmployeeDocumentDownloadPath(employeeId, doc.id),
      reason.trim(),
    );
    deliver(blob, doc.filename);
    return { status: 'delivered', docId: doc.id, filename: doc.filename };
  } catch (cause) {
    return { status: 'refused', docId: doc.id, error: asWorkforceError(cause, 'download failed') };
  }
}

function asWorkforceError(cause: unknown, fallback: string): WorkforceApiError {
  return cause instanceof WorkforceApiError
    ? cause
    : new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: cause instanceof Error ? cause.message : fallback,
        status: 0,
      });
}
